import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { CONFIG } from "@/config";

export const SUPPORTED_LANGUAGES = ["en", "nl", "de", "fr", "es", "it", "pt", "pl"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  nl: "Nederlands",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  pl: "Polski",
};

function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value ?? "");
}

export function getStoredLanguage(): SupportedLanguage {
  const stored = localStorage.getItem("language");
  if (isSupportedLanguage(stored)) return stored;
  return CONFIG.defaultLanguage ?? "en";
}

// Every src/locales/<lang>/<namespace>.json file is picked up automatically,
// so adding a new namespace never requires touching this file.
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "../locales/*/*.json",
  { eager: true },
);

const resourcesByLang = new Map<string, Record<string, Record<string, unknown>>>();
const namespaces = new Set<string>();

for (const [path, module] of Object.entries(localeModules)) {
  const match = /\.\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
  if (!match) continue;
  const lang = match[1];
  const namespace = match[2];
  if (!lang || !namespace) continue;
  const langResources = resourcesByLang.get(lang) ?? {};
  langResources[namespace] = module.default;
  resourcesByLang.set(lang, langResources);
  namespaces.add(namespace);
}

const resources = Object.fromEntries(resourcesByLang);

void i18n.use(initReactI18next).init({
  resources,
  ns: Array.from(namespaces),
  defaultNS: "common",
  lng: getStoredLanguage(),
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
